import { useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Plus } from "lucide-react";
import * as z from "zod/v4";
import { zodValidator } from "@tanstack/zod-adapter";
import { AddAccountModal } from "./-components/AddAccountModal";
import { AddCalendarModal } from "./-components/AddCalendarModal";
import { CalendarList } from "./-components/CalendarList";
import { AccountList } from "./-components/AccountList";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { calendarsQueryOptions } from "@/lib/queries/calendars";
import { zoomConnectionsQueryOptions } from "@/lib/queries/zoom-connections";
import CalendarSvg from "@/components/icons/images/Calendar.svg?url";
import AccountSvg from "@/components/icons/images/Account.svg?url";

const connectionsSearchSchema = z.object({
  tab: z.enum(["calendar", "account"]).catch("calendar"),
});
export const Route = createFileRoute("/_app/linked-accounts/")({
  validateSearch: zodValidator(connectionsSearchSchema),
  component: RouteComponent,
  pendingComponent: LinkedAccountsPageSkeleton,
});

function LinkedAccountsContentSkeleton() {
  return (
    <div
      className="space-y-4 sm:space-y-6"
      role="status"
      aria-label="Loading linked accounts"
    >
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <Skeleton className="h-8 w-64 max-w-full" />
        <Skeleton className="h-10 w-full sm:w-44" />
      </div>

      <div className="overflow-x-auto rounded-lg border bg-card">
        <div className="min-w-[880px]">
          <div className="grid grid-cols-[2fr_0.5fr_1fr_1fr_1fr_1fr] gap-4 bg-muted/50 px-4 py-3">
            {Array.from({ length: 6 }, (_, index) => (
              <Skeleton
                key={index}
                className={index === 0 ? "h-5 w-24" : "h-5 w-16"}
              />
            ))}
          </div>

          {Array.from({ length: 3 }, (_, index) => (
            <div
              key={index}
              className="grid min-h-16 grid-cols-[2fr_0.5fr_1fr_1fr_1fr_1fr] items-center gap-4 border-t px-4 py-3"
            >
              <div className="flex items-center gap-3">
                <Skeleton className="size-10 shrink-0 rounded-full" />
                <Skeleton className="h-5 w-44" />
              </div>
              <Skeleton className="size-3 rounded-full" />
              <Skeleton className="h-5 w-16" />
              <Skeleton className="h-6 w-24 rounded-full" />
              <Skeleton className="h-5 w-24" />
              <Skeleton className="ml-auto h-9 w-24" />
            </div>
          ))}
        </div>
      </div>
      <span className="sr-only">Loading...</span>
    </div>
  );
}

function LinkedAccountsPageSkeleton() {
  return (
    <div className="w-full p-4 sm:p-6">
      <div className="mb-6 grid h-10 grid-cols-2 rounded-lg bg-muted p-1">
        <Skeleton className="h-full w-full bg-background" />
        <div />
      </div>
      <LinkedAccountsContentSkeleton />
    </div>
  );
}

function RouteComponent() {
  const { tab = "calendar" } = Route.useSearch();
  const navigate = useNavigate({
    from: Route.fullPath,
  });
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [addAccountModalOpen, setAddAccountModalOpen] = useState(false);

  const calendarsQuery = useQuery(calendarsQueryOptions());
  const calendars = calendarsQuery.data || [];

  const connectionsQuery = useQuery(zoomConnectionsQueryOptions());
  const connections = connectionsQuery.data || [];

  return (
    <div className="w-full p-4 sm:p-6">
      <Tabs
        value={tab}
        onValueChange={(value) =>
          navigate({
            search: { tab: value as "calendar" | "account" },
            replace: true,
          })
        }
        className="w-full"
      >
        <TabsList className="mb-6 w-full">
          <TabsTrigger value="calendar" className="flex-1">
            Calendars
          </TabsTrigger>
          <TabsTrigger value="account" className="flex-1">
            Meeting Platforms
          </TabsTrigger>
        </TabsList>

        <TabsContent value="calendar">
          {calendarsQuery.isLoading ? (
            <LinkedAccountsContentSkeleton />
          ) : calendars.length === 0 ? (
            <div className="flex h-[calc(100vh-280px)] flex-col items-center justify-center">
              <img src={CalendarSvg} className="mb-6 h-44 w-44" />
              <h2 className="mb-6 text-lg font-medium text-muted-foreground">
                No Calendar Connected
              </h2>
              <Button
                onClick={() => setAddModalOpen(true)}
                className="bg-pink-500 hover:bg-pink-600"
                size="lg"
              >
                <Plus className="mr-2 h-5 w-5" />
                Add a Calendar
              </Button>
            </div>
          ) : (
            <div className="space-y-4 sm:space-y-6">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <h1 className="text-xl font-bold sm:text-2xl">
                  Connect Your Calendar
                </h1>
                <Button
                  onClick={() => setAddModalOpen(true)}
                  className="w-full bg-pink-500 hover:bg-pink-600 sm:w-auto"
                >
                  <Plus className="mr-2 h-4 w-4" />
                  Add a Calendar
                </Button>
              </div>
              <CalendarList calendars={calendars} />
            </div>
          )}
        </TabsContent>

        <TabsContent value="account">
          {connectionsQuery.isLoading ? (
            <LinkedAccountsContentSkeleton />
          ) : connections.length === 0 ? (
            <div className="flex h-[calc(100vh-280px)] flex-col items-center justify-center">
              <img src={AccountSvg} className="mb-6 h-44 w-44" />
              <h2 className="mb-6 text-lg font-medium text-muted-foreground">
                No Accounts Connected
              </h2>
              <Button
                onClick={() => setAddAccountModalOpen(true)}
                className="bg-pink-500 hover:bg-pink-600"
                size="lg"
              >
                <Plus className="mr-2 h-5 w-5" />
                Add an Account
              </Button>
            </div>
          ) : (
            <div className="space-y-4 sm:space-y-6">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <h1 className="text-xl font-bold sm:text-2xl">
                  Your Connected Accounts
                </h1>
                <Button
                  onClick={() => setAddAccountModalOpen(true)}
                  className="w-full bg-pink-500 hover:bg-pink-600 sm:w-auto"
                >
                  <Plus className="mr-2 h-4 w-4" />
                  Connect an Account
                </Button>
              </div>
              <AccountList connections={connections} />
            </div>
          )}
        </TabsContent>
      </Tabs>

      <AddCalendarModal open={addModalOpen} onOpenChange={setAddModalOpen} />
      <AddAccountModal
        open={addAccountModalOpen}
        onOpenChange={setAddAccountModalOpen}
      />
    </div>
  );
}
